import { left, right } from '@sweet-monads/either';
import type { Either } from '@sweet-monads/either';

import { AbstractError } from '@/domain/errors/abstract-error.js';
import { ServerError } from '@/domain/errors/server-error.js';
import type { DashboardDataModel } from '@/domain/models/dashboard.js';
import type { NotificationListResponseModel } from '@/domain/models/notification.js';
import type { CreateReasonParams, ReasonModel, UpdateReasonParams } from '@/domain/models/reason.js';
import type { DashboardRepository } from '@/domain/repositories/dashboard-repository.js';
import type { NotificationRepository } from '@/domain/repositories/notification-repository.js';
import type { ReasonRepository } from '@/domain/repositories/reason-repository.js';
import type { LoadDashboard } from '@/domain/use-cases/dashboard.js';
import type { GetUnreadCount, LoadNotifications, MarkAllNotificationsRead, MarkNotificationRead } from '@/domain/use-cases/notifications.js';
import type { CreateReason, LoadReasons, ToggleReason, UpdateReason } from '@/domain/use-cases/reasons.js';

export class LoadDashboardUseCase implements LoadDashboard {
    constructor(private readonly repository: DashboardRepository) {}

    public async execute(params?: LoadDashboard.Params): Promise<Either<AbstractError, DashboardDataModel>> {
        try {
            return right(await this.repository.load(params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LoadNotificationsUseCase implements LoadNotifications {
    constructor(private readonly repository: NotificationRepository) {}

    public async execute(params?: { page?: number; limit?: number; filter?: 'all' | 'unread' | 'read' }): Promise<Either<AbstractError, NotificationListResponseModel>> {
        try {
            return right(await this.repository.findAll(params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class MarkNotificationReadUseCase implements MarkNotificationRead {
    constructor(private readonly repository: NotificationRepository) {}

    public async execute(id: string): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.markRead(id);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class MarkAllNotificationsReadUseCase implements MarkAllNotificationsRead {
    constructor(private readonly repository: NotificationRepository) {}

    public async execute(): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.markAllRead();
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class GetUnreadCountUseCase implements GetUnreadCount {
    constructor(private readonly repository: NotificationRepository) {}

    public async execute(): Promise<Either<AbstractError, { count: number }>> {
        try {
            return right(await this.repository.getUnreadCount());
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LoadReasonsUseCase implements LoadReasons {
    constructor(private readonly repository: ReasonRepository) {}

    public async execute(params?: { category?: string; isActive?: boolean }): Promise<Either<AbstractError, ReasonModel[]>> {
        try {
            return right(await this.repository.findAll(params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class CreateReasonUseCase implements CreateReason {
    constructor(private readonly repository: ReasonRepository) {}

    public async execute(params: CreateReasonParams): Promise<Either<AbstractError, ReasonModel>> {
        try {
            return right(await this.repository.create(params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UpdateReasonUseCase implements UpdateReason {
    constructor(private readonly repository: ReasonRepository) {}

    public async execute(id: string, params: UpdateReasonParams): Promise<Either<AbstractError, ReasonModel>> {
        try {
            return right(await this.repository.update(id, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ToggleReasonUseCase implements ToggleReason {
    constructor(private readonly repository: ReasonRepository) {}

    public async execute(id: string, active: boolean): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.toggle(id, active);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}
